// English (canonical / fallback) translations.
// All keys must exist here; other locales fall back to this catalog.
export const en: Record<string, string> = {
  // ===== App-wide =====
  'app.name': 'VolleyBot',
  'app.loading': 'Loading…',
  'app.youreAllSet': "You're all set!",
  'app.loadingHome': 'Loading your home page…',

  // ===== Navigation =====
  'nav.home': 'Home',
  'nav.games': 'Games',
  'nav.create': 'Create',
  'nav.profile': 'Profile',
  'nav.admin': 'Admin',
  'nav.test': 'Test',
  'status.open': 'Open',
  'status.full': 'Full',
  'status.going': 'Going',
  'status.cancelled': 'Cancelled',
  'status.finished': 'Finished',

  // ===== Welcome / onboarding =====
  'welcome.title': 'Welcome to VolleyBot',
  'welcome.subtitle': 'Pick your playing level to get started.',
  'welcome.cta': 'Continue',
  'welcome.done.title': "You're all set!",
  'welcome.done.subtitle': 'Loading your home page…',
  'welcome.hello': 'Welcome, {name}!',
  'welcome.intro':
    "VolleyBot helps you find and organize volleyball games in your city. Let's set up your profile in 30 seconds.",
  'welcome.feature.findGames.title': 'Find games near you',
  'welcome.feature.findGames.desc': 'Open games in your city, filtered by skill level.',
  'welcome.feature.join.title': 'Join with one tap',
  'welcome.feature.join.desc': "See who's playing, split the court cost, get reminders.",
  'welcome.feature.organize.title': 'Organize your own game',
  'welcome.feature.organize.desc': 'Pick a venue, time, and skill — invite players in seconds.',
  'welcome.getStarted': 'Get started',
  'welcome.footNote': 'You can always change this later in Profile.',
  'welcome.skill.title': "What's your playing level?",
  'welcome.skill.subtitle':
    "Pick the level that best describes how you play. We'll match you with games at similar levels.",
  'welcome.skill.cta': 'Continue',
  'welcome.skill.saving': 'Saving…',
  // Welcome page when opened from a "change my level" tap.
  'welcome.changeMode.title': 'Change your playing level',
  'welcome.changeMode.subtitle':
    'Pick the level that best describes how you play now. This affects which games are recommended to you.',
  'welcome.changeMode.cta': 'Save level',
  'welcome.changeMode.successTitle': 'Level updated',
  'welcome.changeMode.successSub': 'Your new level is saved. It will be applied to future games.',

  // ===== Skill labels =====
  'skill.LEVEL_1': 'Beginner',
  'skill.LEVEL_2': 'Beginner (Amateur)',
  'skill.LEVEL_3': 'Intermediate',
  'skill.LEVEL_4': 'Advanced',
  'skill.LEVEL_5': 'Semi-Pro',
  'skill.LEVEL_6': 'Professional',
  'skill.LEVEL_1.desc': 'Knows the rules, but basic elements (receive, serve, pass) are inconsistent.',
  'skill.LEVEL_2.desc': 'Overhand serve, positional defense, simple receive.',
  'skill.LEVEL_3.desc': 'Consistent reception, confident attacks, first attempts at a group block.',
  'skill.LEVEL_4.desc': 'Confident play with the setter, first-tempo attacks, powerful & tactical serves.',
  'skill.LEVEL_5.desc': 'Deep tactical understanding, powerful serves / gliders, well-rehearsed combinations.',
  'skill.LEVEL_6.desc': 'Former pro athletes, MS / CMS holders, excellent technique, lightning-fast teamwork.',
  'skill.wheelchair': 'Wheelchair athlete',

  // ===== Home =====
  'home.title': 'Home',
  'home.hello': 'Hello, {name}!',
  'home.botAlerts': 'Get game alerts in the bot',
  'home.botAlertsSub': 'Allow messages so time and game changes reach this chat.',
  'home.upcomingGames': 'Upcoming games',
  'home.seeAll': 'See all',
  'home.noGames': 'No upcoming games yet',
  'home.findGame': 'Find a game',
  'home.createGame': 'Create a game',
  'home.createGameSub': 'Invite players in seconds',
  'home.pickYourLevel': 'Pick your playing level',
  'home.pickYourLevelSub': 'Tell other players how strong you are so they can match you up.',
  'home.openGames': 'Open games',
  'home.freeSpots': 'Free spots',
  'home.allGames': 'All games',
  'home.openGamesInCity': '{count} open games in your city.',
  'home.noGamesSub': 'No games yet - be the first to organize one.',
  'home.emptyCta': 'Be the first to organize a volleyball game in {city}. Tap "Create Game" to start.',
  'home.tapToChangeLevel': 'Tap to change your playing level',
  'home.ratePlayers': 'Rate players',
  'home.ratePlayersSub': 'Help correct skill levels from your last game',

  // ===== Profile =====
  'profile.title': 'Profile',
  'profile.about': 'About you',
  'profile.age': 'Age',
  'profile.city': 'City',
  'profile.language': 'Language',
  'profile.bio': 'Short bio (optional)',
  'profile.skill': 'Playing level',
  'profile.skillFromEvaluations': 'Based on {n} evaluations',
  'profile.save': 'Save',
  'profile.saved': 'Profile saved',
  'profile.signOut': 'Sign out',
  'profile.signOutConfirm': 'Sign out of VolleyBot?',
  'profile.adminPanel': 'Admin panel',
  'profile.blacklist': 'Blocked players',
  'profile.invitations': 'Invitations',
  'profile.payments': 'Payments',
  'profile.status.admin': 'Admin',
  'profile.tapToChangeLevel': 'Tap to change your playing level',

  // ===== Games list =====
  'games.title': 'Games',
  'games.filter.city': 'City',
  'games.filter.skill': 'Skill',
  'games.filter.skillAny': 'Any level',
  'games.filter.bucket.beginner': 'Beginner',
  'games.filter.bucket.intermediate': 'Intermediate',
  'games.filter.bucket.advanced': 'Advanced',
  'games.filter.dateFrom': 'From',
  'games.filter.dateTo': 'To',
  'games.filter.spots': 'Spots',
  'games.filter.any': 'Any',
  'games.filter.search': 'Search notes',
  'games.filter.apply': 'Apply',
  'games.filter.clear': 'Clear',
  'games.filter.quick.all': 'All',
  'games.filter.quick.open': 'Open',
  'games.filter.quick.today': 'Today',
  'games.filter.quick.free': 'Free',
  'games.filter.quick.indoor': 'Indoor',
  'profile.editProfile': 'Edit profile',
  'profile.doneEditing': 'Done',
  'profile.activity': 'Activity',
  'profile.gamesHosted': 'Games hosted',
  'profile.gamesJoined': 'Games joined',
  'games.empty': 'No games match your filters.',

  // ===== Game card =====
  'game.spots': '{count} / {total} spots',
  'game.spotsFull': 'Full',
  'game.waitlistNotify': 'Notify me',
  'game.waitlistSubscribed': 'Watching',
  'game.waitlistStop': 'Stop',
  'game.waitlistHint': 'We’ll message you in Telegram when someone leaves.',
  'game.waitlistToast': 'Got it — we’ll Telegram you if a spot opens.',
  'game.waitlistToastOff': 'Stopped watching this game.',
  'game.changeTime': 'Change time',
  'game.changeTimeTitle': 'Reschedule game',
  'game.changeTimeHint': 'Players on the roster get a Telegram alert with the new time.',
  'game.changeTimeSave': 'Save new time',
  'game.changeTimeCurrent': 'Current: {when}',
  'game.changeTimeInvalid': 'Pick a time in the future.',
  'game.changeTimeSame': 'That’s the same time — nothing to change.',
  'game.changeTimeDone': 'Time updated. Players were notified.',
  'game.edit': 'Edit game',
  'game.editTitle': 'Edit game',
  'game.editHint': 'Update place, type, time, skill, spots, or price. Everyone who joined gets a Telegram message, if they have opened the bot at least once.',
  'game.editPriceHint': '0 keeps the game free. Any higher total is split across the spots.',
  'game.editPriceInvalid': 'Enter a price, or 0 to keep the game free.',
  'game.editSave': 'Save changes',
  'game.editDone': 'Game updated.',
  'game.editNoChanges': 'Nothing changed.',
  'game.editAddressRequired': 'Address is required.',
  'game.editSpotsMin': 'Spots must be at least 2.',
  'game.editSpotsBelowRoster': 'Spots cannot be below current players ({n}).',
  'game.spotsOneLeft': '1 spot left',
  'game.spotsLeft': '{n} spots left',
  'game.perPlayer': '{amount} / player',
  'game.closed': 'Closed lobby',
  'game.closedHint': 'Invite-only — request to join and the host will approve.',
  'game.hidePlayers': 'Hide players from outsiders',
  'game.hidePlayersHint': 'People who have not joined see the roster as Incognito. Joined players still see everyone.',
  'game.playersHiddenBanner': 'Player list is hidden until you join this game.',
  'game.playersHiddenHint': 'Join to see who is playing',
  'game.paid': 'Paid game',
  'game.free': 'Free game',
  'game.host': 'Host',
  'game.perPlayerShort': '/ player',
  'game.playersShort': 'players',
  'game.playersMore': '{count} more',
  'game.spotsLeftShort': '{count} free',
  'game.playType.indoor': 'Indoor',
  'game.playType.outdoor': 'Outdoor',
  'game.playType.beach': 'Beach',
  'game.join': 'Join',
  'game.leave': 'Leave',
  'game.cancel': 'Cancel game',
  'game.finish': 'Finish game',
  'game.requestToJoin': 'Request to join',
  'game.requestPending': 'Request pending',
  'game.joinRequests': 'Join requests ({n})',
  'game.approveRequest': 'Approve',
  'game.rejectRequest': 'Decline',
  'game.finishConfirm': 'Mark this game as finished?',
  'game.cancelConfirm': 'Cancel this game?',
  'game.evaluations': 'Rate players',
  'game.managePayments': 'Track payments',
  'game.invitePlayers': 'Invite players',
  'game.share': 'Share',
  'game.shareHeadline': 'Open game — who’s in?',
  'game.shareCta': 'Open & join:',
  'game.shareSpots': '{filled}/{total} players · {left} free',
  'game.shareSpotsFull': '{filled}/{total} players · full',
  'game.shareFree': 'Free to join',
  'game.shareSkill': 'Level {n}',
  'game.shareUnavailable': 'Sharing isn’t available yet — try again in a moment.',
  'game.reserve': 'Hold a spot',
  'game.reserveHint': 'Saves a seat for someone who can’t join yet. Everyone else only sees “Incognito”.',
  'game.reserveNote': 'Private note',
  'game.reserveNotePlaceholder': 'Who is this for? (only you see this)',
  'game.reserveRemove': 'Release',
  'game.reservedSpots': 'Reserved ({n})',
  'game.incognito': 'Incognito',
  'game.incognitoHint': 'Seat held — not in the app yet',

  // ===== Game detail =====
  'gameDetail.players': 'Players',
  'gameDetail.noPlayers': 'No players yet',
  'gameDetail.address': 'Address',
  'gameDetail.startsAt': 'Starts',
  'gameDetail.notes': 'Notes',
  'gameDetail.cover': 'Venue photo',
  'gameDetail.roleOrganizer': 'Organizer',
  'gameDetail.rolePlayer': 'Player',
  'gameDetail.tagHost': 'HOST',
  'gameDetail.tagYou': 'YOU',
  'gameDetail.reportAction': 'Report',
  'gameDetail.actionsFor': 'Actions for {name}',
  'gameDetail.tabPlayers': 'Players',
  'gameDetail.tabInfo': 'Info',
  'gameDetail.joined': 'Joined',
  'gameDetail.confirmedPlayers': 'Confirmed Players ({n})',
  'gameDetail.pricePerPlayer': 'Per player',

  // ===== Create game =====
  'create.title': 'New game',
  'create.subtitle': 'Fill in the details and invite players',
  'create.field.playType': 'Where will you play',
  'create.playType.indoor': 'Indoor',
  'create.playType.outdoor': 'Outdoor',
  'create.playType.beach': 'Beach',
  'create.section.where': 'Where',
  'create.section.when': 'When',
  'create.section.who': 'Who',
  'create.section.cost': 'Cost',
  'create.section.notes': 'Notes',
  'create.section.covers': 'Field photo',
  'game.coversTitle': 'Field photo',
  'game.coversHint': 'Optional — one court photo. JPEG/PNG/WebP, under 2 MB. Saved on your profile for the next game.',
  'game.coversAdd': 'Add photo',
  'game.coversClear': 'Remove custom photo',
  'game.coversReuseHint': 'Using your saved court photo — tap to replace, or create as-is.',
  'gameDetail.activityTitle': 'Player activity',
  'gameDetail.notifyCount': '{n} pressed Notify me',
  'gameDetail.activityEmpty': 'No join/leave events yet.',
  'gameDetail.activityJoined': '“{name}” entered the game · {when}',
  'gameDetail.activityLeft': '“{name}” left the game · {when}',
  'create.field.venue': 'Venue',
  'create.field.savedPlaces': 'Saved places',
  'create.field.savedPlacesPlaceholder': 'Choose a saved place…',
  'create.field.noSavedPlaces': 'No saved places yet. Your address will be saved after you create the game.',
  'create.field.placeName': 'Place name',
  'create.field.placeNamePlaceholder': 'e.g. Park court or School gym',
  'create.field.venueAddress': 'Address',
  'create.field.venueAddressPlaceholder': 'e.g. ul. Piekary 17, Poznan',
  'create.field.selectVenue': '— select a venue —',
  'create.field.start': 'Start',
  'create.field.duration': 'Hours',
  'create.field.skill': 'Skill level',
  'create.field.spots': 'Total spots',
  'create.field.unlimited': 'No limit',
  'create.field.totalCost': 'Total court cost',
  'create.field.currency': 'Currency',
  'create.field.addressHint': 'Address / meeting point',
  'create.field.addressHintPlaceholder': 'e.g. Side entrance, park behind the mall',
  'create.field.coverPreview': 'Cover photo',
  'create.field.coverPreviewHint': 'Auto-picked from the play type you chose.',
  'create.field.paid': 'This is a paid game',
  'create.paidStatus.on': 'Paid game — players will owe the per-player price.',
  'create.paidStatus.off': 'Free game — leave the cost at 0 to keep it free.',
  'create.field.noVenues': 'No venues in your city yet. Add one from the Games tab.',
  'create.field.closed': 'Closed lobby (invite-only)',
  'create.field.hidePlayers': 'Hide players from outsiders',
  'create.cta': 'Create game',
  'create.cta.creating': 'Creating…',
  'create.cost.auto': 'Auto: {venue} × {hours}h.',
  'create.cost.custom': 'You set a custom amount.',
  'create.cost.empty': 'Select a venue to auto-fill.',
  'create.cost.manual': 'Saved places with pricing will auto-fill the cost. Otherwise you can enter it manually.',
  'create.perPlayer': 'Per player: {amount}',
  'create.splitBetween': 'Split between {n} players',
  'create.unlimitedHint': 'No upper limit on players',
  'create.venueMax': 'Venue max: {n} players',

  // ===== Blacklist =====
  'blacklist.title': 'Blocked players',
  'blacklist.empty': "You haven't blocked anyone.",
  'blacklist.add': 'Block a player',
  'blacklist.reason': 'Reason (optional)',
  'blacklist.remove': 'Unblock',
  'blacklist.maxed': 'You can block up to 10 people. Remove one first.',
  'blacklist.warning.title': 'Blocked player in this game',
  'blacklist.warning.body': 'You blocked {name}. Are you sure you want to join anyway?',
  'blacklist.warning.confirm': 'Join anyway',
  'blacklist.warning.cancel': 'Cancel',

  // ===== Reports =====
  'report.title': 'Report user',
  'report.reason.toxic': 'Toxic behavior',
  'report.reason.skipped': 'Skipped a game',
  'report.reason.harassment': 'Harassment',
  'report.reason.cheating': 'Cheating',
  'report.reason.other': 'Other',
  'report.details': 'Details (optional)',
  'report.submit': 'Submit report',
  'report.thanks': 'Thanks — admins will review this.',

  // ===== Evaluations =====
  'eval.title': 'Rate players',
  'eval.subtitle': 'Rate who you want — tap Skip on anyone you leave out, or just submit the ones you rated.',
  'eval.alreadyRated': 'Already rated',
  'eval.skip': 'Skip',
  'eval.skipAll': 'Skip all',
  'eval.skipPlayer': 'Skip',
  'eval.skipped': 'Skipped',
  'eval.undoSkip': 'Rate',
  'eval.done': 'Done',
  'eval.levelN': 'Lv {n}',
  'eval.noPlayers': 'No co-players to rate.',
  'eval.submit': 'Submit ratings',
  'eval.thanks': 'Thanks — your ratings were submitted.',
  // New "Looks right / Suggest different" UX.
  'eval.looksRight': 'Looks right · Level {n}',
  'eval.suggestDifferent': 'Suggest a different level',

  // ===== Invitations =====
  'invite.title': 'Invitations',
  'invite.empty': 'No pending invitations.',
  'invite.emptySearch': 'No players to show yet — try a name or @username.',
  'invite.accept': 'Accept',
  'invite.decline': 'Decline',
  'invite.ignore': 'Ignore',
  'invite.invitePlayer': 'Invite a player',
  'invite.bannerTitle': 'You have a new game invitation',
  'invite.openGame': 'Open game',
  'invite.invited': 'Invitation sent',
  'invite.invitedTo': 'Invitation sent to {name}',
  'invite.inviteFailed': 'Couldn’t invite {name}',
  'invite.searchPlaceholder': 'Search by name or @username',
  'invite.noResults': 'No players match your search',
  'invite.inviteAction': 'Invite {name}',
  'invite.notifyAria': '{count} pending invitations',
  'invite.sentTitle': 'Sent invites',
  'invite.statusSent': 'Sent',
  'invite.statusRead': 'Read',
  'invite.statusAccepted': 'Accepted',
  'invite.statusDeclined': 'Declined',
  'invite.statusIgnored': 'Ignored',
  'invite.statusInactive': 'Inactive',
  'invite.resend': 'Resend',
  'invite.resendAria': 'Resend invite to {name}',
  'invite.resentTo': 'Invite resent to {name}',

  // ===== Payments =====
  'payments.title': 'Payments',
  'payments.totalCost': 'Total cost',
  'payments.perPlayer': 'Per player',
  'payments.collected': 'Collected',
  'payments.cashCollected': 'Cash collected',
  'payments.methodHint': 'Tap $ for cash or the card for bank transfer. Only one. Tap again to clear (unpaid).',
  'payments.cashHint': 'Tap the cash button when a player pays you in cash at the court.',
  'payments.players': 'Players',
  'payments.status': 'Status',
  'payments.paid': 'Paid',
  'payments.paidCash': 'Cash',
  'payments.paidBank': 'Bank',
  'payments.unpaid': 'Unpaid',
  'payments.markPaid': 'Mark paid',
  'payments.markPaidCash': 'Mark paid with cash',
  'payments.markPaidBank': 'Mark paid by bank transfer',
  'payments.markUnpaid': 'Mark unpaid',
  'payments.myUnpaid': 'You owe {amount} for {venue}',

  // ===== Calendar =====
  'calendar.title': 'Calendar',
  'calendar.today': 'Today',
  'calendar.upcoming': 'Upcoming',
  'calendar.past': 'Past',

  // ===== Language picker =====
  'lang.title': 'Language',
  'lang.uk': 'Українська',
  'lang.pl': 'Polski',
  'lang.en': 'English',
  'lang.ru': 'Москальска',

  // ===== Auth / banned =====
  'auth.banned.title': 'You have been banned',
  'auth.banned.body': 'Reason: {reason}',
  'auth.banned.contact': 'Contact support if you think this is a mistake.',

  // ===== Common =====
  'common.cancel': 'Cancel',
  'common.save': 'Save',
  'common.delete': 'Delete',
  'common.confirm': 'Confirm',
  'common.search': 'Search',
  'common.loading': 'Loading…',
  'common.retry': 'Retry',
  'common.error': 'Something went wrong',
  'common.close': 'Close',
  'common.back': 'Back',
  'common.optional': 'Optional',
  'common.you': 'You',

  // ===== Other player profile =====
  'userProfile.view': 'View profile',
  'userProfile.viewNamed': 'View {name}\'s profile',
  'userProfile.openTelegram': 'Open in Telegram',
  'userProfile.message': 'Message in Telegram',
  'userProfile.block': 'Block player',
  'userProfile.blockConfirm': 'Block {name}? They won\'t appear in your games suggestions.',
  'userProfile.notFound': 'Player not found',
  'userProfile.notFoundText': 'This profile may have been removed or is unavailable.',

  // ===== Banners =====
  'banner.signOut': 'Signed out.',
  'banner.saved': 'Saved.',
  'banner.deleted': 'Deleted.',

  // ===== Admin =====
  'admin.title': 'Admin panel',
  'admin.subtitle': 'Manage users, games, reports, and saved places.',
  'admin.subtitleSection': 'Admin · {section}',
  'admin.users': 'Users',
  'admin.activity': 'App usage',
  'admin.games': 'Games',
  'admin.venues': 'Venues',
  'admin.reports': 'Reports',
  'admin.ratings': 'Ratings given',
  'admin.fb': 'FB',
  'admin.audit': 'Audit',
  'admin.heatmap': 'Heatmap',
  'admin.stats': 'Stats',
  'admin.search': 'Search…',
  'admin.ban': 'Ban',
  'admin.unban': 'Unban',
  'admin.banReason': 'Ban reason',
  'admin.userDetails': 'User details',
  'admin.userSkill': 'Skill score',
  'admin.userSkillHint': 'Set the score shown on their badge. Lock keeps peer ratings from changing it.',
  'admin.userSkillSave': 'Save score',
  'admin.userSkillSaved': 'Score saved.',
  'admin.userSkillClear': 'Clear score',
  'admin.userSkillLock': 'Lock against peer ratings',
  'admin.userWheelchair': 'Show wheelchair icon instead of number',
  'admin.userActivity': 'Activity',
  'admin.appUsage': 'App usage',
  'admin.stat.visited': 'Visited',
  'admin.stat.cancelled': 'Cancelled',
  'admin.stat.created': 'Created',
  'admin.stat.sessionsPerWeek': 'Sessions / wk',
  'admin.stat.entriesDay': 'Entries / day',
  'admin.stat.entriesWeek': 'Entries / week',
  'admin.stat.entriesMonth': 'Entries / month',
  'admin.stat.avgTimeInApp': 'Avg time in app',
  'admin.stat.lastActive': 'Last active',
  'admin.stat.evalGiven': 'Evaluations given',
  'admin.stat.evalReceived': 'Evaluations received',
  'admin.stat.reportsAgainst': 'Reports against',
  'admin.stat.paymentsMade': 'Payments made',
  'admin.stat.evaluatedSkill': 'Evaluated skill',
  'admin.bannedWithReason': 'Banned: {reason}',
  'admin.roleAdmin': 'Admin',
  'admin.badgeBanned': 'Banned',
  'admin.filterBanned': 'Banned',
  'admin.filterActive': 'Active',
  'admin.deleteUser': 'Delete user',
  'admin.deleteUserConfirm': 'Delete user {name}? This cannot be undone.',
  'admin.cancelGame': 'Cancel game',
  'admin.resolve': 'Mark reviewed',
  'admin.dismiss': 'Dismiss',
  'admin.resolveAndBan': 'Mark reviewed & ban',
  'admin.index.stats': 'Users, games, places, and signups',
  'admin.index.users': 'Manage roles, bans, and user activity',
  'admin.index.activity': 'Entries and time spent for every user',
  'admin.index.games': 'Cancel, finish, or review any game',
  'admin.index.venues': 'Review saved places reused in games',
  'admin.index.reports': 'Review and resolve user reports',
  'admin.index.ratings': 'Exact scores a player gave to others',
  'admin.ratingsSearch': 'Search a player…',
  'admin.ratingsPick': 'Search and pick a player to see the scores they gave.',
  'admin.ratingsFrom': 'Scores this player gave',
  'admin.ratingsEmpty': 'This player has not rated anyone yet.',
  'admin.ratingsCount': '{n} given',
  'admin.index.fb': 'This week’s volleyball posts from the Facebook group',
  'admin.fbHint': 'Paste posts from this week. Put a line with only --- between posts. Komplet, Komplekt or Full means the game is full. “przy komplecie” is the price, not a full game.',
  'admin.fbGroup': 'Open the Facebook group',
  'admin.fbPaste': 'Post text',
  'admin.fbLink': 'Link (optional)',
  'admin.fbAdd': 'Add to the board',
  'admin.fbEmpty': 'No posts on the board for this week.',
  'admin.fbStatus': 'Status',
  'admin.fbDate': 'Date',
  'admin.fbLevel': 'Game lvl',
  'admin.fbPlaces': 'Free places',
  'admin.fbAddress': 'Address',
  'admin.fbPostLink': 'Link',
  'admin.fbOpen': 'Open',
  'admin.fbFull': 'Full',
  'admin.fbDelete': 'Remove',
  'admin.fbRaw': 'Original post',
  'admin.fbAdded': 'Added {n}',
  'admin.fbMissing': '—',
  'admin.activityEmpty': 'No users match your search.',
  'admin.activityShowing': 'Showing {shown} of {total}',
  'admin.stat.entriesDayShort': 'Day',
  'admin.stat.entriesWeekShort': 'Week',
  'admin.stat.entriesMonthShort': 'Month',
  'admin.stat.avgTimeShort': 'Avg time',
  'admin.stat.neverActive': 'Never opened the app',
  'admin.stat.activeJustNow': 'Active just now',
  'admin.stat.activeMinutesAgo': 'Active {n}m ago',
  'admin.stat.activeHoursAgo': 'Active {n}h ago',
  'admin.stat.dau': 'DAU',
  'admin.stat.wau': 'WAU',
  'admin.stat.mau': 'MAU',
  'admin.stat.sessionsToday': 'Sessions today',
  'admin.stat.avgSession': 'Avg session',
  'admin.stat.signups24h': 'Signups (24h)',
  'admin.stat.finishedGames': 'Finished games',
  'admin.stat.topScreens': 'Top screens (7d)',
  'admin.stat.topActions': 'Top actions (7d)',
  'admin.stat.topClicks': 'Top clicks (7d)',
  'admin.stat.platforms': 'Platforms (7d)',
  'admin.stat.languages': 'Languages (7d)',
  'admin.stat.rankEmpty': 'No data yet — keep using the app.',
  'admin.index.heatmap': 'Where people tap across screens',
  'admin.heatmapFilter': 'Filter by screen path…',
  'admin.heatmapEmpty': 'No click data yet.',
  'admin.heatmapAvgPos': 'avg tap ({x}, {y})',
  'venue.indoor': 'Indoor',
  'venue.outdoor': 'Outdoor',

  // ===== Errors =====
  'error.network': 'Network error. Please try again.',
  'error.unauthorized': 'Please sign in again.',
  'error.forbidden': 'You do not have permission to do that.',
  'error.notFound': 'Not found.',
  'error.tooLarge': 'The file is too large.',
  'error.unknown': 'Something went wrong.',

  // ===== Empty states =====
  'empty.notSignedIn': 'Not signed in',
  'empty.notSignedInText': 'Open this Mini App from Telegram to set up your profile.',

  // ===== Onboarding banner =====
  'onboarding.chooseLevel': 'Choose your level',
  'onboarding.levelPrompt': 'Pick the level that best describes you. Other players use this to match games.',

  // ===== Cities / defaults =====
  'city.default': 'Default city',

  // ===== Toast / messages =====
  'msg.profileUpdated': 'Profile updated',
  'msg.gameCreated': 'Game created',
  'msg.gameCancelled': 'Game cancelled',
  'msg.gameLeft': 'Left the game',
  'msg.gameJoined': 'Joined the game',
  'msg.blacklistAdded': 'Player blocked',
  'msg.blacklistRemoved': 'Player unblocked',
  'msg.reportSubmitted': 'Report submitted',
  'msg.evaluationsSubmitted': 'Ratings submitted',
  'msg.invitationResponded': 'Invitation updated',
  'msg.paymentUpdated': 'Payment updated',
  'msg.languageUpdated': 'Language updated',
  'msg.locationUpdated': 'Location updated',
  'msg.signOutDone': 'You are signed out',
};
